import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import type { Restaurant, MenuItem } from '@/types';
import { deleteRestaurantData } from './cleanup';

/**
 * Firestore service for Digital Menu SaaS
 * Provides CRUD operations for restaurants and menu items
 */

// Collection references
export const restaurantsCollection = collection(db, 'restaurants');
export const menuItemsCollection = collection(db, 'menuItems');

/**
 * Restaurant Operations
 */

/**
 * Create a new restaurant
 */
export const createRestaurant = async (restaurantData: Omit<Restaurant, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(restaurantsCollection, {
      ...restaurantData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating restaurant:', error);
    throw error;
  }
};

/**
 * Get a restaurant by ID
 */
export const getRestaurant = async (id: string): Promise<Restaurant | null> => {
  try {
    const docRef = doc(db, 'restaurants', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as Restaurant;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting restaurant:', error);
    throw error;
  }
};

/**
 * Get restaurants by owner ID
 */
export const getRestaurantsByOwner = async (ownerId: string): Promise<Restaurant[]> => {
  try {
    const q = query(
      restaurantsCollection,
      where('ownerId', '==', ownerId)
      // Removed orderBy to avoid index issues - we'll sort in frontend
    );
    
    const querySnapshot = await getDocs(q);
    const restaurants: Restaurant[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      restaurants.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as Restaurant);
    });
    
    // Sort by updatedAt descending in frontend
    restaurants.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    
    return restaurants;
  } catch (error) {
    console.error('Error getting restaurants by owner:', error);
    throw error;
  }
};

/**
 * Update a restaurant
 */
export const updateRestaurant = async (id: string, updates: Partial<Omit<Restaurant, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
  try {
    const docRef = doc(db, 'restaurants', id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating restaurant:', error);
    throw error;
  }
};

/**
 * Delete a restaurant and all its related data
 */
export const deleteRestaurant = async (id: string): Promise<void> => {
  try {
    // First, delete all related data (menu sections, items, files)
    await deleteRestaurantData(id);
    
    // Finally, delete the restaurant document itself
    const docRef = doc(db, 'restaurants', id);
    await deleteDoc(docRef);
    
    console.log(`Successfully deleted restaurant ${id} and all related data`);
  } catch (error) {
    console.error('Error deleting restaurant:', error);
    throw error;
  }
};

/**
 * Menu Item Operations
 */

/**
 * Create a new menu item
 */
export const createMenuItem = async (menuItemData: Omit<MenuItem, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(menuItemsCollection, menuItemData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating menu item:', error);
    throw error;
  }
};

/**
 * Get menu items by restaurant ID
 */
export const getMenuItemsByRestaurant = async (restaurantId: string): Promise<MenuItem[]> => {
  try {
    const q = query(
      menuItemsCollection,
      where('restaurantId', '==', restaurantId),
      orderBy('section'),
      orderBy('order')
    );
    
    const querySnapshot = await getDocs(q);
    const menuItems: MenuItem[] = [];
    
    querySnapshot.forEach((doc) => {
      menuItems.push({
        id: doc.id,
        ...doc.data(),
      } as MenuItem);
    });
    
    return menuItems;
  } catch (error) {
    console.error('Error getting menu items:', error);
    throw error;
  }
};

/**
 * Update a menu item
 */
export const updateMenuItem = async (id: string, updates: Partial<Omit<MenuItem, 'id'>>): Promise<void> => {
  try {
    const docRef = doc(db, 'menuItems', id);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error('Error updating menu item:', error);
    throw error;
  }
};

/**
 * Delete a menu item
 */
export const deleteMenuItem = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'menuItems', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting menu item:', error);
    throw error;
  }
};

// Export Firestore instance for direct use if needed
export { db };
